// src/finance/components/LivingBudget.tsx
// Dernier maillon de la chaîne : net d'impôts − dépenses perso = ce qui reste.
// Présentation pure ; les dépenses arrivent déjà mensualisées (monthlyAmountOf).
import type { CSSProperties } from 'react'
import { round2 } from '../engine/tax'
import { fmtEur } from '../format'

const row: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '1rem',
  fontSize: '0.8125rem',
}

const amount: CSSProperties = {
  fontFamily: 'var(--mono)',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
}

export function LivingBudget({
  netMonthly,
  personalExpensesMonthly,
  scenarioLabel,
}: {
  netMonthly: number
  personalExpensesMonthly: number
  /**
   * Scénario et juridiction dont provient `netMonthly`. Le donut de « Qui paie
   * quoi » suit son propre sélecteur : sans cette mention, deux « ce qui te
   * reste » calculés sur des scénarios différents s'affichent côte à côte sous
   * des libellés quasi identiques.
   */
  scenarioLabel?: string | undefined
}) {
  // Sans dépenses saisies, le bloc ne ferait que répéter le net des cartes.
  if (personalExpensesMonthly <= 0) return null

  const remaining = round2(netMonthly - personalExpensesMonthly)
  const isDeficit = remaining < 0
  const tone = isDeficit ? 'var(--charge)' : 'var(--net)'

  return (
    <section
      data-testid="living-budget"
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: '10px',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
      }}
    >
      <h3
        style={{
          fontSize: '0.6875rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          color: 'var(--muted)',
          margin: 0,
        }}
      >
        {scenarioLabel === undefined ? 'Après tes dépenses perso' : `Après tes dépenses perso — ${scenarioLabel}`}
      </h3>

      <div style={row}>
        <span style={{ color: 'var(--muted)' }}>Net d’impôts</span>
        <span style={{ ...amount, color: 'var(--fg)' }}>{fmtEur(netMonthly)}</span>
      </div>
      <div style={row}>
        <span style={{ color: 'var(--living)' }}>Dépenses perso</span>
        <span style={{ ...amount, color: 'var(--living)' }}>{fmtEur(-personalExpensesMonthly)}</span>
      </div>

      <div
        style={{
          background: isDeficit ? 'var(--charge-bg)' : 'var(--net-bg)',
          border: `1px solid ${isDeficit ? 'var(--charge)' : 'var(--net-line)'}`,
          borderRadius: '8px',
          padding: '0.75rem 0.875rem',
          marginTop: '0.25rem',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: tone }}>
            {isDeficit ? 'Il te manque' : 'Il te reste'}
          </div>
          {/* Valeur absolue : « −500 € » sous « il te manque » compterait le signe deux fois. */}
          <div
            data-testid="living-remaining"
            style={{ fontSize: '1.875rem', fontWeight: 700, color: tone, fontFamily: 'var(--mono)', lineHeight: 1.1 }}
          >
            {fmtEur(Math.abs(remaining))}
            <span style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 500 }}> /mois</span>
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: '0.75rem', color: 'var(--muted)' }}>
          {fmtEur(round2(Math.abs(remaining) * 12))} /an
        </div>
      </div>

      {isDeficit && (
        <p style={{ fontSize: '0.75rem', color: 'var(--charge)', margin: 0 }}>
          Tes dépenses perso dépassent ton net d’impôts — il faut facturer plus, ou réduire ce poste.
        </p>
      )}
    </section>
  )
}
